<script>
import PaymentHeader from "./Header";
import OrderData from "./OrderData";
import CycleData from "./CycleData";
import Coupon from "./Coupon";
import ChoosePayment from "./ChoosePayment";
import CreditCard from "./CreditCard";
import Boleto from "./Boleto";
import Preloader from "@/components/_Shared/Preloader/Index";
import BoletoConfirmation from "./BoletoConfirmation";

import orderMixin from "@/mixins/orderMixin";
import scrollMixin from "@/mixins/scrollMixin";
import { mapActions } from "vuex";
import { event } from "vue-analytics";

export default {
  mixins: [orderMixin, scrollMixin],

  props: {
    payable: {
      type: Object,
      required: true
    }
  },

  components: {
    PaymentHeader,
    OrderData,
    CycleData,
    Coupon,
    ChoosePayment,
    CreditCard,
    Boleto,
    Preloader,
    BoletoConfirmation
  },

  data() {
    return {
      payment: null,
      boleto: null,
      preloader: false,
      preloaderProps: {
        title: false,
        message: false
      },
      showBoletoConfirmation: false
    };
  },

  methods: {
    ...mapActions("payments", ["getPayment", "paymentBoleto"]),
    ...mapActions("orders", ["createOnGateway"]),

    async setPayment(payment) {
      this.payment = payment;
      if (payment === "boleto") this.showBoletoConfirmation = true;
    },

    async createBoleto() {
      this.preloader = true;
      this.showBoletoConfirmation = false;

      try {
        this.mustBeCreatedOnGateway(this.payable)
          ? await this.createOnGateway(this.payable.id)
          : await this.createPayment();
      } catch (e) {
        this.$toasted.error(e);
      }
    },

    async createPayment() {
      try {
        if (this.payment != "boleto") return;

        let subscription = this.payable.subscription || null;

        let payable = {
          type: this.payable.type.slice(0, -1),
          starts_at: this.payable.starts_at,
          id: this.payable.id,
          subscription
        };

        await this.paymentBoleto(payable);
      } catch (e) {
        this.$toasted.error(e);
      }
    },

    getPayableBoleto() {
      if (this.payable.payments.length) {
        let hasBoleto = this.payable.payments.find(
          el =>
            el.status === "waiting" && el.funding_instrument.type === "boletos"
        );

        if (hasBoleto) this.$emit("paymentCallback", true);
      }
    },

    scrollToBoleto(ms = 300) {
      setTimeout(() => {
        this.$scrollTo("#payment-method");
      }, ms);
    }
  },

  channels: {
    PaymentChannel: {
      received(data) {
        switch (data.data.to_state) {
          case "waiting":
            if (this.payment === "boleto") {
              this.preloaderProps.title = false;
              this.$emit("refresh");
              this.$emit("paymentCallback", true);
              event("cliente", "click", `Compra:Pagamento:boleto:concluido`);
              fbq("track", "Purchase", { value: 20.0, currency: "BRL" });
            } else {
              this.preloaderProps["title"] = "Pagamento enviado!";
              this.preloaderProps["message"] =
                "Estamos aguardando o retorno do Wirecard.";
            }
            break;

          case "in_analysis":
            if (this.payment === "credit_card") {
              this.preloaderProps["title"] = "Aguarde,";
              this.preloaderProps["message"] =
                "O Wirecard está analisando o pagamento.";
            }
            break;

          case "cancelled":
            this.preloaderProps.title = false;

            event("cliente", "click", `Compra:Pagamento:cancelado`);
            this.$emit("paymentCallback", false);
            break;

          case "authorized":
            this.preloaderProps.title = false;

            event(
              "cliente",
              "click",
              `Compra:Pagamento:cartao_credito:concluido`
            );
            fbq("track", "Purchase", { value: 20.0, currency: "BRL" });
            this.$emit("paymentCallback", true);
            break;

          default:
            break;
        }
      }
    },

    OrderChannel: {
      async received(data) {
        switch (data.data.to_state) {
          case "requested_on_gateway":
            this.preloaderProps["title"] = "Aguarde,";
            this.preloaderProps["message"] =
              "Estamos criando o pedido no Wirecard.";
            break;
          case "created_on_gateway":
            this.preloaderProps["title"] = "Oba!";
            this.preloaderProps["message"] =
              "O pedido foi criado no meio de pagamento. ;)";
            await this.$emit("refresh");
            this.createPayment();
            break;
          case "fail_to_create_on_gateway":
            this.preloaderProps.title = false;
            this.$emit("paymentCallback", false);
            break;
          case "payment_requested":
            this.preloaderProps["title"] = "Quase lá!";
            this.preloaderProps["message"] =
              "Estamos enviando o pagamento no Wirecard!";
            break;
        }
      }
    }
  },

  mounted() {
    this.$cable.subscribe({ channel: "PaymentChannel" });
    this.$cable.subscribe({ channel: "OrderChannel" });
    this.getPayableBoleto();
  },

  watch: {
    payable() {
      this.getPayableBoleto();
    }
  }
};
</script>

<template>
  <div>
    <preloader
      v-if="preloaderProps.title"
      :title="preloaderProps.title"
      :message="preloaderProps.message"
    />

    <payment-header :payable="payable" />
    <order-data v-if="payable.type === 'orders'" :order="payable" />
    <cycle-data v-if="payable.type === 'cycles'" :cycle="payable" />
    <coupon
      v-if="payable.type === 'orders' && (payable.coupon || payable.status != 'created_on_gateway' && !payable.payments.length)"
      :order="payable"
      @couponApplied="$emit('refresh')"
    />
    <choose-payment
      v-if="!boleto"
      @selected="setPayment"
      :payable="payable"
      :preloader="preloader"
    />

    <boleto-confirmation
      :visible="showBoletoConfirmation"
      :payable="payable"
      @cancelled="showBoletoConfirmation = false"
      @confirmed="createBoleto"
    />

    <transition name="zoom" mode="out-in">
      <credit-card id="payment-method" :payable="payable" v-if="payment === 'credit_card'" />
      <boleto id="payment-method" v-else-if="payment === 'boleto' && boleto" :boleto="boleto" />
    </transition>
  </div>
</template>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/_Shared/Checkout/Payment/Index.vue